import { useState, useEffect } from 'react'
import { ScoopMenu, ScoopMenuItem, ScoopMenuDivider, ScoopSearchBar } from '../ui'
import { PlusIcon, CheckIcon, StarIcon, GearIcon } from '@phosphor-icons/react'
import { useWorkspaces } from '../../api/hooks/workspaces/useWorkspaces'
import { useUserInfo } from '../../api/hooks/user/useUserInfo'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { getWorkspaceID } from '../../utils'
import { switchWorkspace, updateWorkspaces } from '../../store/actions/authActions'
import { setResetTrigger } from '../../store/actions/uiActions'
import { ROUTES } from '../../router/routes'
import { useModal, MODALS } from '../modals'
import { EVENTS, trackEvent } from '../../events'
import styles from './SwitchWorkspacePopover.module.scss'

const FAVORITES_STORAGE_KEY = 'scoop_favorite_workspaces'

export const SwitchWorkspacePopover = ({ open, anchorEl, onClose }) => {
    const [searchTerm, setSearchTerm] = useState('')
    const [favoriteWorkspaces, setFavoriteWorkspaces] = useState([])

    const { user } = useUserInfo()
    const { workspaces } = useWorkspaces()
    const workspaceID = getWorkspaceID()
    const createWorkspaceModal = useModal(MODALS.ACCOUNT.CREATE_WORKSPACE)

    const dispatch = useDispatch()
    const navigate = useNavigate()

    const isInternalUser = () => {
        return (
            user?.userName?.includes('@scoopanalytics.com') ||
            user?.userName === 'facundo.rivas.if@gmail.com' ||
            user?.userName === 'juanmarengo@sirius.com.ar'
        )
    }

    // Load favorites from localStorage
    useEffect(() => {
        if (isInternalUser()) {
            const stored = localStorage.getItem(FAVORITES_STORAGE_KEY)
            if (stored) {
                try {
                    setFavoriteWorkspaces(JSON.parse(stored))
                } catch (e) {
                    setFavoriteWorkspaces([])
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user])

    const handleCreateWorkspace = async () => {
        onClose()
        await createWorkspaceModal.show({
            title: 'Create workspace',
            buttonText: 'Create Workspace',
            isCreate: true,
            onSuccess: (newWorkspace, response) => {
                dispatch(updateWorkspaces(response))
                dispatch(switchWorkspace(newWorkspace.workspaceID, newWorkspace.workspaceName))
                dispatch(setResetTrigger())
            },
        })
    }

    const handleSelectWorkspace = (workspaceID) => {
        onClose()
        const workspaceName = getNameFromWorkspaceID(workspaceID)
        if (workspaceName) {
            dispatch(switchWorkspace(workspaceID, workspaceName))
            dispatch(setResetTrigger())
            navigate(ROUTES.INSIGHTS)
            try {
                // userflow.track('workspace_changed', {
                //     workspaceID: workspaceID,
                //     workspaceName: workspaceName,
                // })
                // console.log('sent userflow event', 'switchWorkspace', {
                //     type: 'switchWorkspace',
                //     workspace: {
                //         workspaceID: workspaceID,
                //         name: workspaceName,
                //     },
                // })
            } catch (error) {}
            trackEvent(EVENTS.WORKSPACE.SWITCH, {
                workspace: {
                    workspaceID: workspaceID,
                    name: workspaceName,
                },
            })
        }
    }

    const handleWorkspaceSettings = () => {
        onClose()
        navigate(ROUTES.ACCOUNT, {
            state: {
                selectedTab: 'workspaces',
                selectedWorkspace: workspaceID,
            },
        })
    }

    const getNameFromWorkspaceID = (workspaceID) => {
        const workspace = workspaces.find((workspace) => workspace.workspaceID === workspaceID)
        return workspace ? workspace.name : ''
    }

    const getSortedWorkspaces = () => {
        const sorted = [...workspaces].sort((a, b) => {
            // For internal users, sort favorites to the top
            if (isInternalUser()) {
                const aIsFavorite = favoriteWorkspaces.includes(a.workspaceID)
                const bIsFavorite = favoriteWorkspaces.includes(b.workspaceID)

                if (aIsFavorite && !bIsFavorite) return -1
                if (!aIsFavorite && bIsFavorite) return 1
            }

            // Alphabetical sort within same favorite status
            return a.name.localeCompare(b.name)
        })
        return sorted
    }

    const toggleFavorite = (workspaceID, e) => {
        e.stopPropagation() // Prevent workspace selection when clicking star

        const newFavorites = favoriteWorkspaces.includes(workspaceID)
            ? favoriteWorkspaces.filter((id) => id !== workspaceID)
            : [...favoriteWorkspaces, workspaceID]

        setFavoriteWorkspaces(newFavorites)
        localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(newFavorites))
    }

    const isFavorite = (workspaceID) => {
        return favoriteWorkspaces.includes(workspaceID)
    }

    const filteredWorkspaces = getSortedWorkspaces().filter(
        (workspace) =>
            workspace.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            workspace.workspaceID.toLowerCase().includes(searchTerm.toLowerCase())
    )

    return (
        <>
            <ScoopMenu
                open={open}
                anchorEl={anchorEl}
                onClose={onClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                className={styles.menu}
            >
                <div className={styles.header}>
                    <span className={styles.title}>Switch Workspace</span>
                </div>
                <div className={styles.scrollableContent}>
                    {isInternalUser() && (
                        <div className={styles.searchContainer}>
                            <ScoopSearchBar
                                placeholder="Search"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyDown={(e) => e.stopPropagation()}
                                width="100%"
                                height="26px"
                            />
                        </div>
                    )}
                    {filteredWorkspaces.map((workspace) => (
                        <ScoopMenuItem
                            key={workspace.workspaceID}
                            onClick={() => handleSelectWorkspace(workspace.workspaceID)}
                            selected={workspaceID === workspace.workspaceID}
                            rightSection={
                                workspaceID === workspace.workspaceID ? (
                                    <CheckIcon size={18} className={styles.checkIcon} />
                                ) : null
                            }
                        >
                            <div className={styles.workspaceContent}>
                                {isInternalUser() && (
                                    <StarIcon
                                        size={16}
                                        weight={
                                            isFavorite(workspace.workspaceID) ? 'fill' : 'regular'
                                        }
                                        className={`${styles.starIcon} ${isFavorite(workspace.workspaceID) ? styles.starIconFavorite : ''}`}
                                        onClick={(e) => toggleFavorite(workspace.workspaceID, e)}
                                    />
                                )}
                                <span className={styles.workspaceName}>{workspace.name}</span>
                                {isInternalUser() && (
                                    <span className={styles.workspaceId}>
                                        ({workspace.workspaceID})
                                    </span>
                                )}
                            </div>
                        </ScoopMenuItem>
                    ))}
                </div>
                <ScoopMenuDivider className={styles.divider} />
                <ScoopMenuItem
                    onClick={handleCreateWorkspace}
                    leftSection={<PlusIcon size={18} weight="regular" />}
                >
                    <span className={styles.createWorkspaceText}>Create Workspace</span>
                </ScoopMenuItem>
                <ScoopMenuItem
                    onClick={() => {
                        onClose()
                        handleWorkspaceSettings()
                    }}
                    leftSection={<GearIcon size={18} weight="regular" />}
                >
                    <span className={styles.createWorkspaceText}>Workspace Settings</span>
                </ScoopMenuItem>
            </ScoopMenu>
        </>
    )
}
